import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotify } from 'react-admin';
import { authenticateFromMagicLink } from '@src/apis/reware';
import { completeAuthentication } from '@src/providers/authSession';
import { DashboardAccessError, getSignInErrorMessage } from '@src/providers/authErrors';
import { clearSession } from '@src/providers/authProvider';
import { AuthLayout } from '../AuthLayout';

/*
 * /magic-link-confirm — the confirmation page shown on the requesting device after asking for a sign-in
 * email. The user can either click the emailed link (handled by /magic-link) or type the 6-digit
 * code here. The code is scoped to the email carried in the URL, which also bounds the attempt
 * rate limit on the backend. This page is reachable directly; it reveals nothing about whether the
 * email exists.
 */
export const MagicLinkConfirm = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedCode = code.trim();
    if (!/^\d{6}$/.test(trimmedCode)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    if (!email) {
      setError('This page is missing your email address. Please request a new code.');
      return;
    }

    setLoading(true);
    try {
      const user = await authenticateFromMagicLink({ email, code: trimmedCode });
      const { redirectTo } = await completeAuthentication(user);
      window.location.replace(redirectTo);
    } catch (err) {
      console.error('Magic-link code verification failed:', err);
      // Any failure past a consumed credential must not strand a partial session.
      clearSession();
      const message = getSignInErrorMessage(err, 'That code is incorrect or has expired.');
      // A permissions problem can't be fixed by re-entering the code, so route those users to the
      // badge login instead of leaving them on a page that invites a retry.
      if (err instanceof DashboardAccessError) {
        notify(message, { type: 'error' });
        window.location.replace('/login');
        return;
      }
      setError(message);
      notify(message, { type: 'error' });
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      heading="We emailed you a code"
      subcopy={`We sent an email to ${
        email || 'your address'
      }. Enter the code here or tap the button in the email to continue. If you don’t see the email, check your spam or junk folder.`}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="6-digit code"
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          disabled={loading}
          autoFocus
          autoComplete="one-time-code"
          inputMode="numeric"
          inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
          sx={{ mb: 1.5 }}
        />

        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{ height: 44, fontSize: 15 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Continue'}
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, textAlign: 'center' }}>
        {'Can’t find your code? '}
        <Link component="button" type="button" onClick={() => navigate('/login')}>
          Request a new code.
        </Link>
      </Typography>
    </AuthLayout>
  );
};
